import React from 'react';
import { AutoComplete, Select, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';

import {
  IColumnMapping,
  IImportPlan,
  IPropertyColumn,
  nearestPropertyName,
  TImportTarget,
} from '../../../../utils/asset-type-import';
import { IMapStepProps } from './i-import-wizard';

const TARGET_OPTIONS: { value: TImportTarget; label: string }[] = [
  { value: 'code', label: 'Code' },
  { value: 'description', label: 'Description' },
  { value: 'property', label: 'Property' },
  { value: 'ignore', label: 'Ignore' },
];

const headerCell = (): { style: React.CSSProperties } => ({
  style: {
    padding: '17px 10px',
    background: '#fafafa',
    fontSize: 13,
    fontWeight: 600,
    color: 'rgba(0, 0, 0, 0.85)',
  },
});

const bodyCell = (): { style: React.CSSProperties } => ({
  style: { padding: '8px 10px', fontSize: 13, color: '#535559' },
});

const buildPlan = (columns: IColumnMapping[], plan: IImportPlan): IImportPlan => {
  const codeColumn = columns.findIndex((column) => column.target === 'code');
  const descriptionColumn = columns.findIndex((column) => column.target === 'description');
  const propertyColumns: IPropertyColumn[] = columns
    .filter((column) => column.target === 'property')
    .map((column) => ({ columnIndex: column.columnIndex, name: column.propertyName }));

  const names = new Set(propertyColumns.map((column) => column.name));

  return {
    columns,
    codeColumn,
    descriptionColumn,
    propertyColumns,
    confident: codeColumn >= 0 && descriptionColumn >= 0,
    warnings: plan.warnings,
    propertyRenames: plan.propertyRenames.filter((rename) => names.has(rename.to)),
  };
};

const collectBlockers = (plan: IImportPlan): string[] => {
  const blockers: string[] = [];

  if (plan.codeColumn < 0) blockers.push('Map a column to Code');
  if (plan.descriptionColumn < 0) blockers.push('Map a column to Description');

  const names = plan.propertyColumns.map((property) => property.name.trim());
  if (names.some((name) => name.length === 0)) blockers.push('Every property needs a name');

  const unique = new Set(names.filter((name) => name.length > 0).map((n) => n.toLowerCase()));
  if (unique.size !== names.filter((name) => name.length > 0).length) {
    blockers.push('Property names must be unique');
  }

  return blockers;
};

/**
 * The tenant's spelling of this name, if it already has one. [AVP-891]
 *
 * Property names are free-form server-side and UNIQUE per (assetType, name),
 * so "Colour" next to an existing "Color" is not a near-duplicate -- it is two
 * genuinely separate properties, and the asset type list grows a second column.
 * The import cannot decide that for the user, so it says so instead.
 */
const nameStatus = (
  name: string, existing: string[],
): { known: boolean; suggestion: string | null } => {
  const trimmed = name.trim();
  if (trimmed === '' || existing.length === 0) {
    return { known: false, suggestion: null };
  }

  const needle = trimmed.toLowerCase();
  const known = existing.some((candidate) => candidate.trim().toLowerCase() === needle);
  return {
    known,
    suggestion: known ? null : nearestPropertyName(trimmed, existing),
  };
};

const MapColumnsStep = ({
  plan, samples, advisories, propertyNames, onChange,
}: IMapStepProps): JSX.Element => {
  const blockers = collectBlockers(plan);

  const setTarget = (columnIndex: number, target: TImportTarget): void => {
    const exclusive = target === 'code' || target === 'description';

    const columns = plan.columns.map((column) => {
      if (column.columnIndex === columnIndex) {
        const keepsName = target !== 'property' || column.propertyName.trim().length > 0;
        return {
          ...column,
          target,
          propertyName: keepsName ? column.propertyName : column.header,
        };
      }

      if (exclusive && column.target === target) {
        return {
          ...column,
          target: 'property' as TImportTarget,
          propertyName: column.propertyName.trim().length > 0 ? column.propertyName : column.header,
        };
      }

      return column;
    });

    onChange(buildPlan(columns, plan));
  };

  const setPropertyName = (columnIndex: number, name: string): void => {
    const columns = plan.columns.map((column) => (
      column.columnIndex === columnIndex ? { ...column, propertyName: name } : column
    ));

    onChange(buildPlan(columns, plan));
  };

  const columns: ColumnsType<IColumnMapping> = [
    {
      title: '#',
      key: 'index',
      width: 56,
      onHeaderCell: headerCell,
      onCell: bodyCell,
      render: (_value: unknown, column: IColumnMapping) => column.columnIndex + 1,
    },
    {
      title: 'Header',
      key: 'header',
      onHeaderCell: headerCell,
      onCell: bodyCell,
      render: (_value: unknown, column: IColumnMapping) => (
        <span style={{ fontSize: 13, color: '#535559' }}>{column.header}</span>
      ),
    },
    {
      title: 'Sample',
      key: 'sample',
      onHeaderCell: headerCell,
      onCell: bodyCell,
      render: (_value: unknown, column: IColumnMapping) => {
        const sample = samples[column.columnIndex] ?? '';
        return (
          <span
            title={sample}
            style={{
              display: 'block',
              maxWidth: 220,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: '#8C909B',
            }}
          >
            {sample}
          </span>
        );
      },
    },
    {
      title: 'Maps to',
      key: 'target',
      width: 180,
      onHeaderCell: headerCell,
      onCell: bodyCell,
      render: (_value: unknown, column: IColumnMapping) => (
        <Select<TImportTarget>
          size="small"
          style={{ width: 160 }}
          value={column.target}
          options={TARGET_OPTIONS}
          onChange={(target) => setTarget(column.columnIndex, target)}
        />
      ),
    },
    {
      title: 'Property name',
      key: 'propertyName',
      onHeaderCell: headerCell,
      onCell: bodyCell,
      render: (_value: unknown, column: IColumnMapping) => {
        if (column.target !== 'property') {
          return <AutoComplete size="small" style={{ width: '100%' }} value="" disabled />;
        }

        const { known, suggestion } = nameStatus(column.propertyName, propertyNames);

        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <AutoComplete
                size="small"
                style={{ flex: 1, minWidth: 120 }}
                value={column.propertyName}
                options={propertyNames.map((name) => ({ value: name }))}
                filterOption={(input, option) => String(option?.value ?? '')
                  .toLowerCase()
                  .includes(input.trim().toLowerCase())}
                onChange={(value) => setPropertyName(column.columnIndex, String(value))}
              />
              {column.propertyName.trim() !== '' && propertyNames.length > 0 ? (
                <span style={{ fontSize: 11, color: known ? '#8C909B' : '#D48806', whiteSpace: 'nowrap' }}>
                  {known ? 'existing' : 'new'}
                </span>
              ) : null}
            </div>

            {suggestion ? (
              <div style={{ fontSize: 11, color: '#D48806', marginTop: 2 }}>
                {`This tenant already has \u201c${suggestion}\u201d. `}
                <button
                  type="button"
                  onClick={() => setPropertyName(column.columnIndex, suggestion)}
                  style={{
                    border: 0,
                    background: 'none',
                    padding: 0,
                    color: '#5b47fb',
                    cursor: 'pointer',
                    fontSize: 11,
                  }}
                >
                  Use it
                </button>
              </div>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {advisories.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          {advisories.map((note) => (
            <div key={note} style={{ fontSize: 12, color: '#8C909B' }}>{note}</div>
          ))}
        </div>
      )}

      <div style={{ border: '1px solid #f1f2f5', borderRadius: 11, overflow: 'hidden' }}>
        <Table<IColumnMapping>
          size="small"
          columns={columns}
          dataSource={plan.columns}
          rowKey={(column) => String(column.columnIndex)}
          pagination={false}
        />
      </div>

      {blockers.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {blockers.map((blocker) => (
            <div key={blocker} style={{ fontSize: 12, color: '#E13333' }}>{blocker}</div>
          ))}
        </div>
      )}
    </div>
  );
};

MapColumnsStep.displayName = 'MapColumnsStep';

export default MapColumnsStep;
